// Weekday index, Sunday = 0 (matches Date#getDay and the day integers stored in recurrence rules).
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEKDAY_SHORT_LABELS: Record<Weekday, string> = {
  0: "Sun",
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
};

const MS_PER_DAY = 86_400_000;

export function toISODateString(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function startOfWeek(date: Date): Date {
  const result = new Date(date);
  result.setDate(date.getDate() - date.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
}

export function endOfWeek(date: Date): Date {
  const result = new Date(date);
  result.setDate(date.getDate() + (6 - date.getDay()));
  result.setHours(23, 59, 59, 999);
  return result;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Adds calendar months, clamping the day to the target month's last day so the
 * result never overflows into a later month. Mirrors Rails' `date + n.months`:
 * Jan 31 + 1 month => Feb 28/29, not Mar 3 (which plain `setMonth` would give).
 */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, lastDayOfMonth));
  return result;
}

/** Converts a date to the given timezone and returns the ISO string with timezone offset */
export function convertTimezone(date: Date, timezone: string): string {
  const padding = (num: number): string => num.toString().padStart(2, "0");
  const offset = getTimezoneOffset(date, timezone, padding);

  const year = date.getUTCFullYear();
  const month = padding(date.getUTCMonth() + 1);
  const day = padding(date.getUTCDate());
  const hours = padding(date.getUTCHours());
  const minutes = padding(date.getUTCMinutes());
  const seconds = padding(date.getUTCSeconds());

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offset}`;
}

/**
 * Returns a Date whose *local* getters (getFullYear, getHours, ...) report the
 * wall-clock time of `date` in `timeZone` — i.e. a "fake-local" Date for reading
 * off date/time parts in the practice's timezone.
 *
 * Reads the parts via Intl with a pinned locale, then rebuilds the Date from the
 * numeric parts. Do NOT round-trip through `new Date(date.toLocaleString(...))`:
 * with the host's default locale that produces a string only `new Date()` can
 * parse under en-US (e.g. en-GB yields "23/06/2026, 13:30:00" -> Invalid Date),
 * so non-US-locale browsers silently get an invalid date.
 */
export function toTimezone(date: Date, timeZone: string): Date {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);

  // `hour12: false` emits "24" for midnight in some engines; normalize to 0.
  const hour = get("hour") % 24;

  return new Date(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
}

/**
 * Formats a signing instant for the "Signature dated ..." caption that gets
 * rasterized into the signature image. Pass `timeZone` (an IANA id like
 * "America/New_York") to stamp the time in the practice's timezone instead of
 * the signing device's; omit it to fall back to the device timezone. Pass `locale`
 * (the page's I18n locale) so the caption reads in the language the patient chose
 * rather than the device's; omit it to use the runtime default.
 */
export function formatSignatureTimestamp(date: Date, timeZone?: string, locale?: string): string {
  return date.toLocaleString(locale, timeZone ? { timeZone } : undefined);
}

/**
 * Whole calendar days from `from` to `to`, both "YYYY-MM-DD". Positive when `to` is the later
 * date. Measured between UTC midnights, so neither the reader's timezone nor a DST boundary
 * between the two dates can move the count.
 */
export function daysBetweenDates(from: string, to: string): number {
  return (utcMidnight(to) - utcMidnight(from)) / MS_PER_DAY;
}

/** Whole days elapsed since an ISO8601 time, clamped at zero so a client clock running behind the
 *  server never yields a negative age. */
export function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY));
}

// A "YYYY-MM-DD" string's three numbers, month already zero-based for the Date constructors.
function dateParts(dateString: string): [number, number, number] {
  const [year, month, day] = dateString.split("-").map(Number);
  return [year!, month! - 1, day!];
}

function utcMidnight(dateString: string): number {
  return Date.UTC(...dateParts(dateString));
}

export function toLocalDate(dateString: string): Date {
  const [year, monthIndex, day] = dateParts(dateString);
  return new Date(year, monthIndex, day);
}

export function ageFromDob(dob: string | null): number | null {
  if (!dob) return null;
  const birth = toLocalDate(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

function getTimezoneOffset(date: Date, timezone: string, padding: (num: number) => string): string {
  const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
  const tzDate = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
  const offset = (tzDate.getTime() - utcDate.getTime()) / 60000;
  const sign = offset <= 0 ? "-" : "+";
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `${sign}${padding(hours)}:${padding(minutes)}`;
}

/**
 * Parses a UTC ISO datetime string and returns an "HH:MM" time string
 * interpreted in the specified timezone.
 *
 * This is useful for populating time inputs when the backend stores times in UTC
 * but the UI displays times in a specific timezone
 */
export function parseTimeInTimezone(isoString: string | null, timezone: string): string {
  if (!isoString) return "";

  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
  return `${hour}:${minute}`;
}

/**
 * Parses a UTC ISO datetime string and returns a "YYYY-MM-DD" date string
 * interpreted in the specified timezone.
 *
 * This is useful for populating date inputs when the backend stores dates in UTC
 * but the UI displays dates in a specific timezone
 */
export function parseDateInTimezone(isoString: string | null, timezone: string): string {
  if (!isoString) return "";

  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value ?? "0000";
  const month = parts.find((p) => p.type === "month")?.value ?? "00";
  const day = parts.find((p) => p.type === "day")?.value ?? "00";
  return `${year}-${month}-${day}`;
}

/**
 * Returns today's date as a "YYYY-MM-DD" string in the given timezone.
 * Falls back to UTC if no timezone is provided.
 */
export function todayInTimezone(timezone: string | undefined): string {
  if (!timezone) return new Date().toISOString().slice(0, 10);

  return parseDateInTimezone(new Date().toISOString(), timezone);
}

function timeStringToMinutes(time: string): number | null {
  if (!time) return null;
  const [h, m] = time.split(":").map(Number);
  if (h == null || m == null || Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Adds `minutes` to an "HH:MM" time string and returns the resulting "HH:MM".
 * Returns "" if the input is invalid or the result falls outside a single day
 * (no silent wraparound).
 */
export function addMinutesToTime(time: string, minutes: number): string {
  const base = timeStringToMinutes(time);
  if (base == null || !Number.isFinite(minutes)) return "";
  const total = base + minutes;
  if (total < 0 || total >= 1440) return "";
  const hh = Math.floor(total / 60)
    .toString()
    .padStart(2, "0");
  const mm = (total % 60).toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Signed difference in minutes between two "HH:MM" time strings (`end - start`).
 * Returns null if either input is invalid. Negative results indicate `end` is
 * earlier than `start` and are intentionally surfaced so callers can run their
 * own validation.
 */
export function minutesBetweenTimes(startTime: string, endTime: string): number | null {
  const start = timeStringToMinutes(startTime);
  const end = timeStringToMinutes(endTime);
  if (start == null || end == null) return null;
  return end - start;
}

// "HH:MM:SS" (or "HH:MM") -> seconds since midnight.
export function timeOfDayToSeconds(timeString: string): number {
  const [hours = 0, minutes = 0, seconds = 0] = timeString.split(":").map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}
